/**
 * ADMIN CALENDAR COMPONENT
 * * @description 
 * A private management interface that allows administrators to Create, Read, 
 * and Delete (CRD) events. This data populates the public EventCalendar.
 * * @features
 * - Real-time event publishing to Supabase 'events' table
 * - Points reward management
 * - Automatic list refreshing after mutation
 */

import React, { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";
import { Link } from "react-router-dom";
import { 
  ArrowLeft, 
  Plus, 
  Trash2, 
  Calendar, 
  Link as LinkIcon, 
  Image as ImageIcon, 
  AlignLeft, 
  Users,
  Award 
} from "lucide-react";

export default function AdminCalendar() {
  // --- STATE MANAGEMENT ---
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  
  /** * Initial form state template 
   * Used for both state initialization and form resetting.
   */
  const initialFormState = {
    title: "",
    description: "",
    date: "",
    association_name: "",
    association_logo: "",
    signup_url: "",
    location: "",
    points_reward: 0 
  };

  const [formData, setFormData] = useState(initialFormState);

  // --- LIFECYCLE ---
  useEffect(() => {
    fetchEvents();
  }, []);

  // --- DATA ACTIONS ---

  /**
   * Fetches all events from Supabase ordered by date.
   */
  async function fetchEvents() {
    const { data, error } = await supabase
      .from("events")
      .select("*")
      .order("date", { ascending: true });
    
    if (error) {
      console.error("Error fetching events:", error.message);
      return;
    }
    if (data) setEvents(data);
  }

  /**
   * Handles form submission to create a new event.
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const { error } = await supabase.from("events").insert([formData]);

    if (error) {
      alert("Error adding event: " + error.message);
    } else {
      setFormData(initialFormState); // Reset form
      fetchEvents(); // Refresh list
      alert("Event published successfully!");
    }
    setLoading(false);
  };

  /**
   * Deletes an event by ID after user confirmation.
   * @param {string|number} id - The Supabase record ID.
   */
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this event? This action cannot be undone.")) {
      const { error } = await supabase.from("events").delete().eq("id", id);
      if (error) {
        alert("Error deleting event: " + error.message);
      } else {
        fetchEvents();
      }
    }
  };

  // --- STYLING (THEME: MODERN MINIMALIST) ---
  const styles = {
    container: { 
      minHeight: "100vh", 
      backgroundColor: "#FFFFFF", 
      padding: "1.5rem", 
      fontFamily: "'Inter', sans-serif",
      maxWidth: "800px", // Standardizing width for admin desktop use
      margin: "0 auto" 
    },
    header: { 
      display: "flex", 
      alignItems: "center", 
      gap: "1rem", 
      marginBottom: "2rem" 
    },
    form: { 
      display: "flex", 
      flexDirection: "column", 
      gap: "1rem", 
      backgroundColor: "#F8FAFB", 
      padding: "1.5rem", 
      borderRadius: "24px", 
      marginBottom: "3rem", 
      border: "1px solid #E2E8F0" 
    },
    inputGroup: { 
      display: "flex", 
      alignItems: "center", 
      gap: "10px", 
      backgroundColor: "white", 
      padding: "0.8rem 1rem", 
      borderRadius: "14px", 
      border: "1px solid #E2E8F0" 
    },
    input: { 
      border: "none", 
      outline: "none", 
      width: "100%", 
      fontSize: "0.95rem", 
      color: "#2D302D", 
      backgroundColor: "transparent" 
    },
    textarea: { 
      border: "none", 
      outline: "none", 
      width: "100%", 
      fontSize: "0.95rem", 
      minHeight: "80px", 
      resize: "none", 
      backgroundColor: "transparent", 
      fontFamily: "inherit" 
    },
    submitBtn: { 
      backgroundColor: "#12873B", 
      color: "white", 
      border: "none", 
      padding: "1rem", 
      borderRadius: "16px", 
      fontWeight: "800", 
      cursor: "pointer", 
      display: "flex", 
      alignItems: "center", 
      justifyContent: "center", 
      gap: "8px", 
      marginTop: "0.5rem" 
    },
    eventItem: { 
      display: "flex", 
      justifyContent: "space-between", 
      alignItems: "center", 
      padding: "1.2rem 0", 
      borderBottom: "1px solid #F1F5F9" 
    },
    deleteBtn: { 
      background: "none", 
      border: "none", 
      color: "#E5243B", 
      cursor: "pointer", 
      padding: "8px",
      transition: "opacity 0.2s"
    }
  };

  return (
    <div style={styles.container}>
      {/* NAVIGATION HEADER */}
      <div style={styles.header}>
        <Link to="/" style={{ color: "#12873B" }}>
          <ArrowLeft size={28} />
        </Link>
        <h1 style={{ fontSize: "1.5rem", fontWeight: "900", margin: 0 }}>Event Manager</h1>
      </div>

      {/* CREATION FORM */}
      <form onSubmit={handleSubmit} style={styles.form}>
        <h3 style={{ margin: "0 0 0.5rem 0", color: "#12873B", fontWeight: "800" }}>Create New Event</h3>
        
        <div style={styles.inputGroup}>
          <Calendar size={18} color="#94A3B8" />
          <input 
            type="text" 
            placeholder="Event Title" 
            style={styles.input} 
            required 
            value={formData.title} 
            onChange={(e) => setFormData({...formData, title: e.target.value})} 
          />
        </div>

        <div style={styles.inputGroup}>
          <AlignLeft size={18} color="#94A3B8" />
          <textarea 
            placeholder="Event Description" 
            style={styles.textarea} 
            required 
            value={formData.description} 
            onChange={(e) => setFormData({...formData, description: e.target.value})} 
          />
        </div>

        <div style={{ display: "flex", gap: "10px" }}>
          <div style={{ ...styles.inputGroup, flex: 1 }}>
            <input 
              type="date" 
              style={styles.input} 
              required 
              value={formData.date} 
              onChange={(e) => setFormData({...formData, date: e.target.value})} 
            />
          </div>
          <div style={{ ...styles.inputGroup, flex: 1 }}>
            <Award size={18} color="#12873B" />
            <input 
              type="number" 
              placeholder="Points" 
              style={styles.input} 
              required 
              value={formData.points_reward} 
              onChange={(e) => setFormData({...formData, points_reward: parseInt(e.target.value) || 0})} 
            />
          </div>
        </div>

        <div style={styles.inputGroup}>
          <Users size={18} color="#94A3B8" />
          <input 
            type="text" 
            placeholder="Association Name" 
            style={styles.input} 
            required 
            value={formData.association_name} 
            onChange={(e) => setFormData({...formData, association_name: e.target.value})} 
          />
        </div>

        <div style={styles.inputGroup}>
          <ImageIcon size={18} color="#94A3B8" />
          <input 
            type="url" 
            placeholder="Logo URL (optional)" 
            style={styles.input} 
            value={formData.association_logo} 
            onChange={(e) => setFormData({...formData, association_logo: e.target.value})} 
          />
        </div>

        <div style={styles.inputGroup}>
          <LinkIcon size={18} color="#94A3B8" />
          <input 
            type="url" 
            placeholder="Signup URL (optional)" 
            style={styles.input} 
            value={formData.signup_url} 
            onChange={(e) => setFormData({...formData, signup_url: e.target.value})} 
          />
        </div>

        <button type="submit" style={styles.submitBtn} disabled={loading}>
          {loading ? "Publishing..." : <><Plus size={20} /> Publish Event</>}
        </button>
      </form>

      {/* ACTIVE EVENTS LIST */}
      <h3 style={{ color: "#12873B", marginBottom: "1rem", fontWeight: "800" }}>
        Active Events ({events.length})
      </h3>
      <div>
        {events.map(event => (
          <div key={event.id} style={styles.eventItem}>
            <div>
              <div style={{ fontWeight: "700", color: "#2D302D" }}>{event.title}</div>
              <div style={{ fontSize: "0.85rem", color: "#94A3B8", marginTop: "4px" }}>
                {event.date} • {event.association_name} • <strong>{event.points_reward} pts</strong>
              </div>
            </div>
            <button 
              onClick={() => handleDelete(event.id)} 
              style={styles.deleteBtn}
              aria-label="Delete event"
            >
              <Trash2 size={20} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}